import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ArrowLeft, Clock, RefreshCw, CheckCircle2 } from "lucide-react";
import { verifyPartnerOtp, requestPartnerOtp } from "../../api/partner/partnerApi";
import {
  setPartnerAuthenticated,
  setPartnerError,
  setPartnerLoading,
} from "../../features/partnerOnboarding/partnerOnboardingSlice";
import { loginSuccess } from "../../features/auth/authSlice";
import { showToast } from "../../components/common/notification/Notification";
import PartnerLayout from "../../components/partner/PartnerLayout";
import PartnerStepIndicator from "../../components/partner/PartnerStepIndicator";
import "./PartnerPages.css";

export const PartnerVerifyOtp = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const storedMobile = useSelector((state) => state.partnerOnboarding.mobileNumber);
  const mobileNumber = location.state?.mobile || storedMobile || "";

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(120); // 2 minutes countdown
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const inputRefs = useRef([...Array(6)].map(() => React.createRef()));

  // Redirect to signup if no mobile number provided
  useEffect(() => {
    if (!mobileNumber) {
      navigate("/partner/signup", { replace: true });
    }
  }, [mobileNumber, navigate]);

  // Countdown timer
  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => setTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleOtpChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, "");
    if (!val) return;

    const newOtp = [...otp];
    newOtp[idx] = val.slice(-1);
    setOtp(newOtp);

    // Auto-focus next input
    if (idx < 5 && val) {
      inputRefs.current[idx + 1].current?.focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];
      if (otp[idx]) {
        newOtp[idx] = "";
        setOtp(newOtp);
      } else if (idx > 0) {
        inputRefs.current[idx - 1].current?.focus();
        newOtp[idx - 1] = "";
        setOtp(newOtp);
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      inputRefs.current[idx - 1].current?.focus();
    } else if (e.key === "ArrowRight" && idx < 5) {
      inputRefs.current[idx + 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasteData) {
      const newOtp = [...otp];
      for (let i = 0; i < 6; i++) {
        newOtp[i] = pasteData[i] || "";
      }
      setOtp(newOtp);
      const focusIndex = Math.min(pasteData.length, 5);
      inputRefs.current[focusIndex].current?.focus();
    }
  };

  const handleVerify = async (e) => {
    if (e) e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length !== 6) {
      showToast("Please enter a valid 6-digit OTP code.", { type: "error" });
      return;
    }

    setIsVerifying(true);
    dispatch(setPartnerLoading(true));

    try {
      const response = await verifyPartnerOtp(mobileNumber, otpValue);

      if (response?.statusType === "SUCCESS" || response?.status === "SUCCESS") {
        showToast("Mobile verified successfully!", { type: "success" });

        const sessionPayload = {
          statusType: "SUCCESS",
          accessToken: response.accessToken || response.token,
          token: response.token || response.accessToken,
          refreshToken: response.refreshToken,
          user: response.user,
          userDetails: response.user,
        };

        // Update Redux auth & partner slices
        dispatch(loginSuccess(sessionPayload));
        dispatch(setPartnerAuthenticated(sessionPayload));

        // Determine destination based on user profile state (exact mobile logic)
        const user = response.user || {};
        const checkProfileCompletion = (u) => {
          const required = ["first_name", "last_name", "email_address", "service_id"];
          for (const field of required) {
            const val = u[field];
            if (field === "service_id") {
              if (!val || val === "" || val === null || val === undefined || val === 0 || val === "0") {
                return false;
              }
            } else {
              if (!val || String(val).trim() === "") {
                return false;
              }
            }
          }
          return true;
        };

        const isProfileComplete = checkProfileCompletion(user);

        if (!isProfileComplete) {
          navigate("/partner/onboarding", { replace: true });
        } else {
          const subscriptionData =
            response.subscription && typeof response.subscription === "object"
              ? response.subscription
              : (response.subscription || response.ahdUserSubscription || null);
          const hasActiveSub = Boolean(subscriptionData && subscriptionData.status !== "EXPIRED");
          if (hasActiveSub) {
            navigate("/partner/workspace", { replace: true });
          } else {
            navigate("/partner/subscription", { replace: true, state: { serviceId: user.service_id } });
          }
        }
      } else {
        const errorMsg = response?.text || response?.message || "The OTP is incorrect or has expired. Please try again.";
        showToast(errorMsg, { type: "error" });
        dispatch(setPartnerError(errorMsg));
      }
    } catch (err) {
      console.error("OTP verification error:", err);
      const msg = err?.response?.data?.text || err?.response?.data?.message || "The OTP is incorrect or has expired. Please try again.";
      showToast(msg, { type: "error" });
      dispatch(setPartnerError(msg));
    } finally {
      setIsVerifying(false);
      dispatch(setPartnerLoading(false));
    }
  };

  const handleResend = async () => {
    if (timer > 0 || isResending) return;

    setIsResending(true);
    try {
      const response = await requestPartnerOtp(mobileNumber, "register");
      if (response?.statusType === "SUCCESS" || response?.status === "SUCCESS") {
        setTimer(120);
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0].current?.focus();
        showToast("New OTP sent successfully!", { type: "success" });
      } else {
        showToast(response?.text || "Failed to resend OTP.", { type: "error" });
      }
    } catch (err) {
      showToast("Unable to resend OTP. Please try again later.", { type: "error" });
    } finally {
      setIsResending(false);
    }
  };

  const formatTimer = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  return (
    <PartnerLayout
      currentStepIndex={2}
      title="Verify Your Mobile Number"
      subtitle={`We sent a 6-digit verification code to +91 ${mobileNumber || "your number"}.`}
      visualTitle="Fast & Secure Verification"
      visualSubtitle="Your registered mobile number helps us secure your partner account, leads, and transaction payouts."
    >
      <div className="partner-form-container">
        <div className="partner-verify-target-row">
          <div className="partner-verify-target-badge">
            <span>+91 {mobileNumber}</span>
          </div>
          <Link to="/partner/signup" className="partner-verify-change-btn">
            Change number
          </Link>
        </div>

        <form onSubmit={handleVerify} className="partner-form">
          <div className="partner-form__group text-center">
            <label className="partner-form__label text-center mb-2">
              Enter 6-digit OTP code
            </label>
            <div className="partner-otp-grid" onPaste={handlePaste}>
              {otp.map((digit, idx) => (
                <input
                  key={idx}
                  ref={inputRefs.current[idx]}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  className={`partner-otp-input ${digit ? "partner-otp-input--filled" : ""}`}
                  onChange={(e) => handleOtpChange(e, idx)}
                  onKeyDown={(e) => handleKeyDown(e, idx)}
                  autoFocus={idx === 0}
                />
              ))}
            </div>
          </div>

          <div className="partner-timer-section">
            {timer > 0 ? (
              <div className="partner-timer-badge">
                <Clock size={15} />
                <span>Code expires in {formatTimer(timer)}</span>
              </div>
            ) : (
              <button
                type="button"
                className="partner-resend-btn"
                onClick={handleResend}
                disabled={isResending}
              >
                <RefreshCw size={14} className={isResending ? "partner-spin" : ""} />
                <span>{isResending ? "Resending code..." : "Resend OTP Code"}</span>
              </button>
            )}
          </div>

          <div className="partner-sub-btn-group mt-3">
            <Link
              to="/partner/signup"
              className="partner-sub-btn partner-sub-btn--outline"
            >
              <ArrowLeft size={13} />
              <span>Back</span>
            </Link>
            <button
              type="submit"
              className="partner-sub-btn partner-sub-btn--primary"
              disabled={otp.join("").length !== 6 || isVerifying}
            >
              {isVerifying ? (
                <span className="partner-spinner">Verifying Code...</span>
              ) : (
                <>
                  <span>Verify & Continue</span>
                  <CheckCircle2 size={16} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </PartnerLayout>
  );
};

export default PartnerVerifyOtp;
